import React from 'react';
import { Page, Text, View, Document, StyleSheet } from '@react-pdf/renderer';
import { format } from 'date-fns/format';

const styles = StyleSheet.create({
    page: {
        padding: 20,
        fontSize: 10,
        fontFamily: 'Helvetica',
    },
    header: {
        marginBottom: 20,
    },
    title: {
        fontSize: 18,
        textAlign: 'center',
        marginBottom: 10,
    },
    reportInfo: {
        marginBottom: 15,
    },
    infoRow: {
        flexDirection: 'row',
        marginBottom: 5,
    },
    label: {
        width: 120,
        fontWeight: 'bold',
    },
    table: {
        display: 'flex',
        width: '100%',
        borderStyle: 'solid',
        borderWidth: 1,
        borderColor: '#000',
        marginTop: 10,
    },
    tableRow: {
        flexDirection: 'row',
        borderBottomWidth: 1,
        borderBottomColor: '#000',
    },
    tableHeader: {
        backgroundColor: '#f0f0f0',
        fontWeight: 'bold',
    },
    tableCell: {
        padding: 5,
        borderRightWidth: 1,
        borderRightColor: '#000',
        flex: 1,
        flexShrink: 1,
        flexBasis: 0,
        minWidth: 0,
        fontSize: 8,
    },
    footer: {
        border: '1px solid #ccc',
        position: 'absolute',
        bottom: 20,
        left: 20,
        right: 20,
        textAlign: 'center',
        fontSize: 8,
    },
    summarySection: {
        marginTop: 20,
        padding: 10,
        borderTop: 1,
        borderTopColor: '#000',
    },
    summaryTitle: {
        fontSize: 12,
        fontWeight: 'bold',
        marginBottom: 8,
    },
    summaryItem: {
        marginBottom: 5,
        flexDirection: 'row',
        justifyContent: 'space-between',
        fontWeight: 'bold',
    },
    positiveAmount: {
        color: '#008000',  // Green color
        fontWeight: 'bold',
    },
    negativeAmount: {
        color: '#FF0000',  // Red color
        fontWeight: 'bold',
    },
});

interface GenericReportPDFProps {
    title: string;
    reportType: string;
    gameDetails?: {
        gameName?: string;
        resultTime?: string;
        gameType?: string;
    };
    dateRange?: {
        startDate?: Date;
        endDate?: Date;
    };
    columns: Array<{
        header: string;
        field: string;
        render?: (row: any) => string;
    }>;
    data: any[];
    summary?: {
        totalBetAmount?: number;
        totalWinAmount?: number;
        profitLoss?: number;
        agentCommission?: number;
        adminCommission?: number;
    };
}

const GenericReportPDF: React.FC<GenericReportPDFProps> = ({
    title,
    reportType,
    gameDetails,
    dateRange,
    columns,
    data,
    summary
}) => (
    <Document>
        <Page size="A4" style={styles.page}>
            <View style={styles.header}>
                <Text style={styles.title}>{title}</Text>

                <View style={styles.reportInfo}>
                    <View style={styles.infoRow}>
                        <Text style={styles.label}>Report Type:</Text>
                        <Text>{reportType}</Text>
                    </View>
                    <View style={styles.infoRow}>
                        <Text style={styles.label}>Generated Date:</Text>
                        <Text>{format(new Date(), 'dd-MM-yyyy hh:mm a')}</Text>
                    </View>
                    {dateRange && (
                        <View style={styles.infoRow}>
                            <Text style={styles.label}>Date Range:</Text>
                            <Text>
                                {dateRange.startDate && format(dateRange.startDate, 'dd-MM-yyyy')}
                                {dateRange.endDate && ` to ${format(dateRange.endDate, 'dd-MM-yyyy')}`}
                            </Text>
                        </View>
                    )}
                    {gameDetails && (
                        <>
                            {gameDetails.gameName && (
                                <View style={styles.infoRow}>
                                    <Text style={styles.label}>Game:</Text>
                                    <Text>{gameDetails.gameName}</Text>
                                </View>
                            )}
                            {gameDetails.resultTime && (
                                <View style={styles.infoRow}>
                                    <Text style={styles.label}>Result Time:</Text>
                                    <Text>{gameDetails.resultTime}</Text>
                                </View>
                            )}
                            {gameDetails.gameType && (
                                <View style={styles.infoRow}>
                                    <Text style={styles.label}>Game Type:</Text>
                                    <Text>{gameDetails.gameType}</Text>
                                </View>
                            )}
                        </>
                    )}
                </View>
            </View>

            <View style={styles.table}>
                <View style={[styles.tableRow, styles.tableHeader]}>
                    {columns.map((column, index) => (
                        <Text key={index} style={[styles.tableCell, { fontWeight: 'bold' }]}>{column.header}</Text>
                    ))}
                </View>

                {data.map((row, rowIndex) => (
                    <View key={rowIndex} style={styles.tableRow}>
                        {columns.map((column, colIndex) => {
                            const cellValue = column.render ? column.render(row) : row[column.field];
                            // Format numeric values to 2 decimal places
                            const formattedValue = typeof cellValue === 'number' 
                                ? cellValue.toFixed(2)
                                : cellValue;
                                
                            return (
                                <Text 
                                    key={colIndex} 
                                    style={[
                                        styles.tableCell,
                                        { textAlign: ['winPoint', 'customerCommission', 'agentCommission', 'adminCommission', 'totalCommission'].includes(column.field) ? 'right' : 'left' }
                                    ]}
                                >
                                    {formattedValue}
                                </Text>
                            );
                        })}
                    </View>
                ))}
            </View>

            {summary && (
                <View style={styles.summarySection}>
                    <Text style={styles.summaryTitle}>Summary</Text>
                    {summary.totalBetAmount !== undefined && (
                        <Text style={styles.summaryItem}>
                            Total Bet Amount: {typeof summary.totalBetAmount === 'number' ? summary.totalBetAmount.toFixed(2) : summary.totalBetAmount}
                        </Text>
                    )}
                    {summary.totalWinAmount !== undefined && (
                        <Text style={styles.summaryItem}>
                            Total Win Amount:{' '}
                            <Text style={summary.totalWinAmount >= 0 ? styles.positiveAmount : styles.negativeAmount}>
                                {typeof summary.totalWinAmount === 'number' ? 
                                    (summary.totalWinAmount >= 0 ? '+' : '') + summary.totalWinAmount.toFixed(2) 
                                    : summary.totalWinAmount
                                }
                            </Text>
                        </Text>
                    )}
                    {summary.agentCommission !== undefined && (
                        <Text style={styles.summaryItem}>
                            Agent Commission:{' '}
                            <Text style={summary.agentCommission >= 0 ? styles.positiveAmount : styles.negativeAmount}>
                                {typeof summary.agentCommission === 'number' ? 
                                    (summary.agentCommission >= 0 ? '+' : '') + summary.agentCommission.toFixed(2) 
                                    : summary.agentCommission
                                }
                            </Text>
                        </Text>
                    )}
                    {summary.adminCommission !== undefined && (
                        <Text style={styles.summaryItem}>
                            Admin Commission:{' '}
                            <Text style={summary.adminCommission >= 0 ? styles.positiveAmount : styles.negativeAmount}>
                                {typeof summary.adminCommission === 'number' ? 
                                    (summary.adminCommission >= 0 ? '+' : '') + summary.adminCommission.toFixed(2) 
                                    : summary.adminCommission
                                }
                            </Text>
                        </Text>
                    )}
                    {summary.profitLoss !== undefined && (
                        <Text style={styles.summaryItem}>
                            Profit/Loss:{' '}
                            <Text style={summary.profitLoss >= 0 ? styles.positiveAmount : styles.negativeAmount}>
                                {typeof summary.profitLoss === 'number' ? 
                                    (summary.profitLoss >= 0 ? '+' : '') + summary.profitLoss.toFixed(2) 
                                    : summary.profitLoss
                                }
                            </Text>
                        </Text>
                    )}
                </View>
            )}


            <Text style={styles.footer}>
                Generated by BizzBoy.in on {format(new Date(), 'dd-MM-yyyy hh:mm a')}
            </Text>
        </Page>
    </Document>
);

export default GenericReportPDF;